import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  useAppStore,
  type AppType,
  type PermissionAction,
  type PermissionModule,
} from '../../stores/app-store';
import { useAuthStore } from '../../stores/auth-store';

interface PermissionGuardProps {
  children: ReactNode;
  module: PermissionModule;
  action?: PermissionAction;
  app?: AppType; // If not provided, uses current app
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Permission guard that checks if user has access to a module/action.
 * Can be used to wrap pages or components.
 */
export function PermissionGuard({
  children,
  module,
  action,
  app,
  fallback,
  redirectTo,
}: PermissionGuardProps) {
  const { currentApp, hasPermission, hasPermissionForApp } = useAppStore();

  const targetApp = app || currentApp;

  // Check permission
  const hasAccess = targetApp
    ? hasPermissionForApp(targetApp, module, action)
    : hasPermission(module, action);

  if (!hasAccess) {
    // If redirect specified, navigate there
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // If fallback provided, render it
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default: render nothing
    return null;
  }

  return <>{children}</>;
}

interface AppGuardProps {
  children: ReactNode;
  app: AppType;
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * App guard that checks if user has access to a specific app.
 * Used to protect entire app sections.
 */
export function AppGuard({ children, app, fallback, redirectTo }: AppGuardProps) {
  const { hasAppAccess } = useAppStore();

  if (!hasAppAccess(app)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return null;
  }

  return <>{children}</>;
}

/**
 * Hook to check permissions in components
 */
export function usePermission(module: PermissionModule, action?: PermissionAction, app?: AppType) {
  const { hasPermission, hasPermissionForApp } = useAppStore();
  const { pathname } = useLocation();
  // A bookmarked URL can differ from the app selection persisted by an earlier visit.
  const target = app ?? (pathname === '/trading' || pathname.startsWith('/trading/') ? 'TRADING'
    : pathname === '/travel' || pathname.startsWith('/travel/') ? 'TRAVEL' : undefined);
  return target ? hasPermissionForApp(target, module, action) : hasPermission(module, action);
}

/**
 * Hook to check app access
 */
export function useAppAccess(app: AppType) {
  const { hasAppAccess } = useAppStore();
  return hasAppAccess(app);
}

/**
 * No Access fallback component
 */
export function NoAccessFallback({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-[50vh] text-center">
      <div className="text-6xl mb-4">🔒</div>
      <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
      <p className="text-muted-foreground max-w-md">
        {message ||
          "You don't have permission to access this page. Please contact your administrator if you believe this is an error."}
      </p>
    </div>
  );
}

interface AdminGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Admin guard that checks if user has SUPER_ADMIN or ADMIN role.
 * Used to protect admin-only pages like settings/permissions.
 */
export function AdminGuard({ children, fallback, redirectTo }: AdminGuardProps) {
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

  if (!isAdmin) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return <NoAccessFallback message="This page is restricted to administrators only." />;
  }

  return <>{children}</>;
}

/**
 * Hook to check if current user is admin
 */
export function useIsAdmin() {
  const user = useAuthStore((state) => state.user);
  return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
}
